import {objectFromEntries} from '@benchling/js-utils';
import classNames from 'classnames';
import React from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {assert} from 'Asserts';
import DataConnector from 'connect/DataConnector';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as NavConstants from 'constants/NavConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import getConfig from 'getConfig';
import {SearchSection} from 'graphql-client/__generated/graphqlGlobalTypes';
import {Pagination} from 'lib/Listings';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {LoadingState} from 'models/reduceLoadingState';
import {CrumbLink, SplitBreadcrumbs} from 'nav/Breadcrumbs';
import ListingHeader from 'nav/sidepanel/header/ListingHeader';
import SidepanelListingSettingsDropdown from 'nav/sidepanel/header/SidepanelListingSettingsDropdown';
import CompactListingBanner from 'nav/sidepanel/listing/CompactListingBanner';
import CompactResultsPagerWrapper from 'nav/sidepanel/listing/CompactResultsPagerWrapper';
import ListingToolbarArea from 'nav/sidepanel/listing/ListingToolbarArea';
import {getNavigationURL} from 'nav/sidepanel/listing/SearchURLs';
import SelectedRowsCounter from 'nav/sidepanel/listing/SelectedRowsCounter';
import SidepanelItemList from 'nav/sidepanel/listing/SidepanelItemList';
import {getSelectedPageSize} from 'nav/sidepanel/PageSizeUtil';
import ToggleDropdown from 'nav/ToggleDropdown';
import BenchlingProfiler from 'performance/BenchlingProfiler';
import {composeSidepanelSearchResultsRenderedEvent} from 'performance/processSearchEvents';
import {getStore} from 'redux-stores/Store';
import canRequestsBeExecuted from 'requests/canRequestsBeExecuted';
import ExecuteButton from 'requests/execution/ExecuteButton';
import ExecuteRequestFlow from 'requests/execution/ExecuteRequestFlow';
import {RequestSampleStatusCounts} from 'requests/fulfillments/FulfillmentConstants';
import useAllRequestTaskSchemasQuery from 'requests/graphql/useAllRequestTaskSchemasQuery';
import useAllRequestTemplatesQuery from 'requests/graphql/useAllRequestTemplatesQuery';
import {
  getLegacyRequestObjectName,
  Request,
  RequestSchema,
  SCHEMA_ID_REQUEST_SCHEMA_ALL,
  SCHEMA_OPTION_REQUEST_SCHEMA_ALL,
} from 'requests/RequestConstants';
import {getSortedRequestTaskSchemas} from 'requests/schemas/reduceRequestSchemas';
import * as RequestTaskSchemaReduxActions from 'requests/schemas/RequestTaskSchemaReduxActions';
import * as AssaySchemaActions from 'results/AssaySchemaActions';
import AssayStore from 'results/AssayStore';
import ClearSearchButton from 'search/ClearSearchButton';
import * as FilterUtil from 'search/FilterUtil';
import QueryInput from 'search/QueryInput';
import SavedSearchHeader from 'search/saved-searches/SavedSearchHeader';
import {SavedSearch} from 'search/saved-searches/SavedSearchUtil';
import SaveSearchButton from 'search/saved-searches/SaveSearchButton';
import ShareSearchButton from 'search/saved-searches/ShareSearchButton';
import {
  Filter,
  FilterKey,
  keyToEmptyFilter,
  LISTING_ERROR_MESSAGE,
  SearchParams,
  SearchType,
  SingleValueFilter,
  SortKey,
} from 'search/SearchConstants';
import * as SearchPreferences from 'search/SearchPreferences';
import {trackSelectedSearchResult} from 'search/searchTracking';
import * as SearchUtil from 'search/SearchUtil';
import withSearching, {NavigationUpdate, SearchProps} from 'search/withSearching';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import * as nyc from 'ui-library/NYC';
import ResultsPager from 'ui-library/ResultsPager';
import {cycleColumnHeaderSort} from 'ui-library/scrollable/ScrollableTableUtil';

import {RequestTemplateGraphQL} from '../../../graphql-fragments/requests/__generated/RequestTemplateGraphQL';
import {RequestTaskSchemaGraphQL} from '../../../graphql-fragments/requests/schemas/__generated/RequestTaskSchemaGraphQL';
import ExpandedRequestTable from './ExpandedRequestTable';
import NewRequestDropdown from './NewRequestDropdown';
import openCreateRequestModal, {OpenCreateModalArgs} from './openCreateRequestModal';
import RequestsFiltersContainer from './RequestsFiltersContainer';
import RequestsToolbar from './RequestsToolbar';

export function defaultRequestsSearchParams(): SearchParams {
  return {
    filters: [
      {...keyToEmptyFilter(FilterKey.ASSAY_SCHEMA), value: SCHEMA_ID_REQUEST_SCHEMA_ALL},
      {...keyToEmptyFilter(FilterKey.TYPES), value: [ModelTypeConstants.REQUEST]},
    ],
    limit: getSelectedPageSize(),
    offset: 0,
    query: '',
    sorts: SearchPreferences.getAppliedSortsFromUserPreference(NavConstants.Section.REQUESTS),
  };
}

interface QueryingRequestsSidepanelProps {
  isExpanded: boolean;
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  activeItemId: string | null;
}

interface RequestsSidepanelProps extends QueryingRequestsSidepanelProps {
  requestTemplatesById: Record<string, RequestTemplateGraphQL>;
  requestTaskSchemasById: Record<string, RequestTaskSchemaGraphQL>;
}

interface EnhancedProps {
  fetchRequestTaskSchemas: Function; // TODO(TypeScript): Define more specific function type
  isFetchingRequests: boolean;
  onOpenCreateModal: (openModalArgs: OpenCreateModalArgs) => void;
  requests: Array<Request> | null;
  requestSchemas: Array<RequestSchema> | null;
  requestedTaskSchemaIdsByRequestId: {[requestId: string]: Array<string>};
  sampleStatusCountsByRequestId: {[requestId: string]: RequestSampleStatusCounts};
}

type Props = RequestsSidepanelProps & SearchProps & EnhancedProps;

const fetchRequestTaskSchemas = (): void => {
  getStore().dispatch(RequestTaskSchemaReduxActions.fetchAll());
};

const onOpenCreateModal = (openModalArgs: OpenCreateModalArgs): void => {
  getStore().dispatch(openCreateRequestModal(openModalArgs));
};

class RequestsSidepanelConnector extends DataConnector<RequestsSidepanelProps & SearchProps, EnhancedProps> {
  override stores = [AssayStore];

  enhanceProps(): EnhancedProps {
    const state = this.reduxState();
    const {sampleStatusCountsByRequestId, requestedTaskSchemaIdsByRequestId} = state.models.requests.inputs;
    const requestSchemas = AssayStore.getRequestSchemas();

    const enhancedProps = {
      fetchRequestTaskSchemas,
      isFetchingRequests: true,
      onOpenCreateModal,
      requests: null,
      requestSchemas,
      requestTaskSchemasById: this.props.requestTaskSchemasById,
      requestedTaskSchemaIdsByRequestId,
      sampleStatusCountsByRequestId,
    };

    if (!this.props.searchResults) {
      return enhancedProps;
    }
    const requestsById = this.getOrFetchMultipleOptionalDeprecated(
      ManagedModelType.REQUEST_BY_ID,
      this.props.searchResults.ids
    );
    const requests = this.props.searchResults.ids.map((id) => requestsById[id]);
    return {
      ...enhancedProps,
      isFetchingRequests: false,
      requests: requests.every((request) => Boolean(request)) ? requests : null,
    };
  }
}

function RequestsSidepanelCrumbs(props: {
  activeSavedSearch?: SavedSearch;
  onSelectSavedSearch: (savedSearch?: SavedSearch) => Promise<void>;
  onSearch: (searchParams: SearchParams) => void;
  searchParams: SearchParams;
}): JSX.Element {
  if (props.activeSavedSearch) {
    return (
      <SplitBreadcrumbs
        crumbs={[
          <CrumbLink
            key="requests"
            text={getLegacyRequestObjectName()}
            onClick={() => {
              props.onSearch(
                SearchUtil.clearSearchParams(props.searchParams, {
                  preservedFilterKeys: [],
                  filtersToReset: defaultRequestsSearchParams().filters,
                })
              );
              props.onSelectSavedSearch(undefined);
            }}
          />,
          <CrumbLink key="root" text={props.activeSavedSearch.name} />,
        ]}
      />
    );
  }

  return (
    <SplitBreadcrumbs
      crumbs={[<span key="root">{getLegacyRequestObjectName({plural: true, capwords: true})}</span>]}
    />
  );
}

export class RequestsSidepanel extends React.Component<Props> {
  override componentDidMount(): void {
    AssaySchemaActions.fetchAllAssaySchemas();
    this.props.fetchRequestTaskSchemas();
  }

  override shouldComponentUpdate(nextProps: Props): boolean {
    const omittedPropNames = [
      'fetchRequestTaskSchemas',
      'onOpenCreateModal',
      'onRefreshListing',
      'onSearch',
      'onUpdateFilters',
      'onUpdateOffset',
      'onUpdateQueryString',
      'onUpdateSortOrder',
      'onUpdatePageSize',
      'onUpdateGroupBy',
      'onSelectIds',
      'onToggleItemsExpanded',
      'onRequestMoreChildItems',
      'childPageSize',
    ];
    return (
      // eslint-disable-next-line no-restricted-syntax
      !isEqual(_.omit(this.props, omittedPropNames), _.omit(nextProps, omittedPropNames))
    );
  }

  _getSelectedSchemaId(): string | null {
    const schemaFilter = this.props.searchParams.filters.find(
      (filter: Filter) => filter.key === FilterKey.ASSAY_SCHEMA
    ) as SingleValueFilter;
    return schemaFilter ? schemaFilter.value : null;
  }

  _getSelectedSchema(): RequestSchema | typeof SCHEMA_OPTION_REQUEST_SCHEMA_ALL | null {
    if (!this.props.requestSchemas) {
      return null;
    }
    return this._getSelectedSchemaId() === SCHEMA_ID_REQUEST_SCHEMA_ALL
      ? SCHEMA_OPTION_REQUEST_SCHEMA_ALL
      : _.findWhere(this.props.requestSchemas, {id: this._getSelectedSchemaId()}) || null;
  }

  _handleSearch = (searchParams: SearchParams): void => {
    this.props.onSearch(searchParams);
    SearchPreferences.rememberAppliedSortsToUserPreference(NavConstants.Section.REQUESTS, searchParams.sorts);
  };

  _handleChangeSortFromKey(sortKey: string): void {
    const newSort = cycleColumnHeaderSort(this.props.searchParams.sorts, sortKey);
    this.props.onUpdateSortOrder([newSort]);
  }

  _openExecuteRequestsModal(): void {
    assert(this.props.requests != null, 'Expected requests to be non-null');
    LegacyModalContainer.open(
      ExecuteRequestFlow,
      <ExecuteRequestFlow
        // eslint-disable-next-line no-restricted-syntax
        requests={this.props.selectedIds.map((id) => _.find(this.props.requests!, {id})!)}
        initialFilters={this.props.searchParams.filters}
        requestSchemasByRequestId={
          this._getSelectedRequestSchemasByRequestId() as {[requestId: string]: RequestSchema}
        }
        onRequestClose={() => {
          LegacyModalContainer.close(ExecuteRequestFlow);
        }}
      />
    );
  }

  _handleClear = (): void => {
    this._handleSearch(
      SearchUtil.clearSearchParams(this.props.searchParams, {
        preservedFilterKeys: [],
        filtersToReset: defaultRequestsSearchParams().filters,
      })
    );
    if (this.props.activeSavedSearch) {
      // Clear saved search
      this.props.onSelectSavedSearch(undefined);
    }
  };

  _isClearEnabled = (): boolean => {
    const schemaFilterValue = FilterUtil.getFilterValueByKey(
      this.props.searchParams.filters,
      FilterKey.ASSAY_SCHEMA
    );
    return (
      SearchUtil.hasQueryOrNonDefaultFilters(
        this.props.searchParams,
        [FilterKey.ASSAY_SCHEMA],
        [ModelTypeConstants.REQUEST]
      ) ||
      !schemaFilterValue ||
      schemaFilterValue !== SCHEMA_ID_REQUEST_SCHEMA_ALL
    );
  };

  _getPagination(): Pagination | null {
    if (this.props.searchParams && this.props.searchResults) {
      return {
        offset: this.props.searchParams.offset,
        limit: this.props.searchParams.limit,
        total: this.props.searchResults.total,
        isExact: this.props.searchResults.isExact,
      };
    } else {
      return null;
    }
  }

  _renderResultsPager(): JSX.Element | null {
    const pagination = this._getPagination();
    if (!this.props.searchParams || !this.props.searchResults || !this.props.requests || !pagination) {
      return null;
    }
    return (
      <ResultsPager
        isLoading={this.props.isFetchingRequests}
        pagination={pagination}
        setOffset={this.props.onUpdateOffset}
      />
    );
  }

  _getSelectedRequestSchemasByRequestId(): {[requestId: string]: RequestSchema | null} | null {
    if (this.props.requests == null || this.props.requestSchemas == null) {
      return null;
    }
    const requestSchemasByRequestId: {[requestId: string]: RequestSchema | null} = {};
    for (const request of this._getSelectedRequests()) {
      const schema = _.findWhere(this.props.requestSchemas, {id: request.schemaId});
      requestSchemasByRequestId[request.id] = schema || null;
    }
    return requestSchemasByRequestId;
  }

  *_getSelectedRequests(): IterableIterator<Request> {
    if (!this.props.requests) {
      return;
    }
    for (const request of this.props.requests) {
      if (this.props.selectedIds.includes(request.id)) {
        yield request;
      }
    }
  }

  _getEmptyMessage = (): string =>
    this.props.loadingState === LoadingState.FAILED
      ? LISTING_ERROR_MESSAGE
      : `No ${getLegacyRequestObjectName({lowerCase: true, plural: true})}`;

  _renderExecuteButton(): JSX.Element {
    const requestIdToRequestHasSamples: {[id: string]: boolean} = {};
    const requests = Array.from(this._getSelectedRequests());
    for (const request of requests) {
      const sampleCounts = this.props.sampleStatusCountsByRequestId[request.id];
      requestIdToRequestHasSamples[request.id] =
        Object.values(sampleCounts).filter((count) => count !== 0).length > 0;
    }
    const {executeEnabled, explanationMessage} = canRequestsBeExecuted({
      requests,
      requestSchemaByRequestId: this._getSelectedRequestSchemasByRequestId() as {
        [requestId: string]: RequestSchema;
      },
      requestIdToRequestHasSamples,
    });

    return (
      <ExecuteButton
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        className="u-mini-spacing-right"
        disabled={!executeEnabled}
        onClick={() => {
          this._openExecuteRequestsModal();
        }}
        tooltipTitle={explanationMessage}
      />
    );
  }

  _renderSettingsDropdown(): JSX.Element {
    return (
      <SidepanelListingSettingsDropdown
        onSetSortOrder={this.props.onUpdateSortOrder}
        onSetPageSize={this.props.onUpdatePageSize}
        searchParams={this.props.searchParams}
        availableSortKeys={[
          SortKey.SORT_CREATED_AT_KEY,
          SortKey.SORT_SCHEDULED_ON_KEY,
          SortKey.SORT_IDENTIFIER_KEY,
        ]}
        displaySettingsConfig={{
          isCompact: this.props.isCompactDisplay,
          setIsCompactDisplay: this.props.setIsCompactDisplay,
        }}
      />
    );
  }

  _renderSidepanelContents(): JSX.Element {
    if (!this.props.searchParams || !this.props.requestTaskSchemasById || !this.props.requestSchemas) {
      return <CenteredHorizontalThrobber />;
    } else {
      const saveSearchButton =
        !this.props.activeSavedSearch &&
        // TODO(Search): Rename this to better reflect usage.
        this._isClearEnabled() ? (
          <SaveSearchButton
            searchSection={SearchSection.REQUESTS}
            activeSavedSearch={this.props.activeSavedSearch}
            onSaved={this.props.onSelectSavedSearch}
            searchParams={this.props.searchParams}
          />
        ) : undefined;
      const shareSearchButton =
        getConfig('ENABLE_SIGHTGLASS_SHARED_SAVED_SEARCH') &&
        this.props.activeSavedSearch &&
        this.props.isExpanded ? (
          <ShareSearchButton
            searchSection={SearchSection.REQUESTS}
            activeSavedSearch={this.props.activeSavedSearch}
            onSaved={this.props.onSelectSavedSearch}
            onDeleted={() => {
              this.props.onSelectSavedSearch(undefined);
            }}
          />
        ) : undefined;
      const header = (
        <ListingHeader
          buttons={
            <div>
              {shareSearchButton}
              <ToggleDropdown
                className="u-mini-spacing-left u-mini-spacing-right"
                expanded={this.props.isExpanded}
              />
              {this.props.isExpanded ? this._renderExecuteButton() : null}
              <NewRequestDropdown
                schemas={this.props.requestSchemas}
                onOpenCreateModal={this.props.onOpenCreateModal}
                requestTemplatesById={this.props.requestTemplatesById}
                requestTaskSchemasById={this.props.requestTaskSchemasById}
              />
            </div>
          }
          crumbs={
            <RequestsSidepanelCrumbs
              activeSavedSearch={this.props.activeSavedSearch}
              onSelectSavedSearch={this.props.onSelectSavedSearch}
              onSearch={this.props.onSearch}
              searchParams={this.props.searchParams}
            />
          }
          savedSearchHeader={
            <SavedSearchHeader
              searchSection={SearchSection.REQUESTS}
              onSelectSavedSearch={this.props.onSelectSavedSearch}
              activeSavedSearch={this.props.activeSavedSearch}
              key="saved-searches-dropdown"
              onSearch={this.props.onSearch}
              searchParams={this.props.searchParams}
              isExpanded={this.props.isExpanded}
            />
          }
          isExpanded={this.props.isExpanded}
          queryInput={
            <QueryInput
              onUpdateQuery={this.props.onUpdateQueryString}
              query={this.props.searchParams.query}
            />
          }
          filtersContainer={
            <RequestsFiltersContainer
              allTaskSchemas={getSortedRequestTaskSchemas(this.props.requestTaskSchemasById)}
              assaySchemas={this.props.requestSchemas}
              filters={this.props.searchParams.filters}
              filterErrors={this.props.filterErrors}
              isExpanded={this.props.isExpanded}
              onChangeFilters={this.props.onUpdateFilters}
              selectedAssaySchema={this._getSelectedSchema()}
              saveSearchButton={saveSearchButton}
              clearButton={this._isClearEnabled() ? <ClearSearchButton onClear={this._handleClear} /> : null}
            />
          }
          settingsDropdown={this._renderSettingsDropdown()}
        />
      );
      const SIDEPANEL_SEARCH_RESULTS_RENDERED_EVENT = composeSidepanelSearchResultsRenderedEvent();
      if (this.props.isExpanded) {
        return (
          <>
            <nyc.Row fluid={true}>
              <nyc.Col lg={1} />
              <nyc.Col lg={20} className="u-flex-display u-vertical-flex">
                {header}
                <div className="u-lr-split u-small-spacing-top">
                  <div className="u-lr-split">
                    <div className="u-flex-display">{this._renderResultsPager()}</div>
                    {this._renderSettingsDropdown()}
                  </div>
                  <ListingToolbarArea>
                    <SelectedRowsCounter numSelected={this.props.selectedIds.length} />
                    {this.props.requests && (
                      <RequestsToolbar selectedRequests={Array.from(this._getSelectedRequests())} />
                    )}
                  </ListingToolbarArea>
                </div>
                <div className="u-flex-grow u-flex-display u-small-spacing-top">
                  <BenchlingProfiler {...SIDEPANEL_SEARCH_RESULTS_RENDERED_EVENT}>
                    <ExpandedRequestTable
                      isCompact={this.props.isCompactDisplay}
                      emptyMessage={this._getEmptyMessage()}
                      requests={this.props.requests}
                      schemas={this.props.requestSchemas}
                      sorts={this.props.searchParams.sorts}
                      onChangeSort={(sortKey: string) => {
                        this._handleChangeSortFromKey(sortKey);
                      }}
                      onUpdateSelectedRequestIds={this.props.onSelectIds}
                      requestTaskSchemasById={this.props.requestTaskSchemasById}
                      selectedRequestIds={this.props.selectedIds}
                      sampleStatusCountsByRequestId={this.props.sampleStatusCountsByRequestId}
                      requestedTaskSchemaIdsByRequestId={this.props.requestedTaskSchemaIdsByRequestId}
                      searchResults={this.props.searchResults}
                    />
                  </BenchlingProfiler>
                </div>
              </nyc.Col>
              <nyc.Col lg={1} />
            </nyc.Row>
          </>
        );
      } else {
        return (
          <>
            <nyc.Row>{header}</nyc.Row>
            <nyc.Row>
              {this._isClearEnabled() && (
                <CompactListingBanner
                  total={this.props.searchResults ? this.props.searchResults.total : null}
                  isExact={this.props.searchResults ? this.props.searchResults.isExact : null}
                />
              )}
            </nyc.Row>
            <nyc.Row fluid={true} className="u-vertical-flex">
              <div className="u-flex-grow u-position-relative">
                <div className="u-absolute-scroll-container">
                  {(() => {
                    const pagination = this._getPagination();
                    if (this.props.searchResults == null || !pagination) {
                      return <CenteredHorizontalThrobber />;
                    } else {
                      return (
                        <CompactResultsPagerWrapper
                          pagination={pagination}
                          setOffset={this.props.onUpdateOffset}
                        >
                          <BenchlingProfiler {...SIDEPANEL_SEARCH_RESULTS_RENDERED_EVENT}>
                            <SidepanelItemList
                              activeItemId={this.props.activeItemId}
                              emptyMessage={this._getEmptyMessage()}
                              finderId={null}
                              isCompact={this.props.isCompactDisplay}
                              isSearch={false}
                              items={this.props.requests || []}
                              openResult={(request) => {
                                trackSelectedSearchResult(request.id, this.props.searchResults);
                                FileBrowserActions.openFile(request.id);
                              }}
                              onToggleExpandedItems={this.props.onToggleItemsExpanded}
                              onRequestMoreChildItems={this.props.onRequestMoreChildItems}
                              childPageSize={this.props.childPageSize ?? 0}
                              folderId={null}
                              section={null}
                              asyncFetcherIsFetchedState={this.props.asyncFetcherIsFetchedState}
                              onUpdateAsyncFetcherIsFetchedState={
                                this.props.onUpdateAsyncFetcherIsFetchedState
                              }
                            />
                          </BenchlingProfiler>
                        </CompactResultsPagerWrapper>
                      );
                    }
                  })()}
                </div>
              </div>
            </nyc.Row>
          </>
        );
      }
    }
  }

  override render(): JSX.Element | null {
    return (
      <nyc.Container
        className={classNames('sidepanel', {expandedContainer: this.props.isExpanded})}
        size={this.props.isExpanded ? 'lg' : 'sidepanel'}
        dataTestAttrs={{
          component: 'RequestsSidepanel',
          element: 'root',
          key: this.props.isExpanded ? 'expanded' : 'collapsed',
        }}
      >
        {this._renderSidepanelContents()}
      </nyc.Container>
    );
  }
}

export function getRequestsURL(prevProps: Props, props: Props): NavigationUpdate | null {
  return props.isExpanded
    ? {
        url: getNavigationURL('/requests', props.searchParams, props.filterErrors),
        options: {replace: prevProps.isExpanded === props.isExpanded},
      }
    : null;
}

function QueryingRequestsSidepanel(
  props: QueryingRequestsSidepanelProps & EnhancedProps & SearchProps
): JSX.Element {
  const requestTemplates = useAllRequestTemplatesQuery();
  const requestTaskSchemas = useAllRequestTaskSchemasQuery();

  const requestTemplatesById = objectFromEntries(
    (requestTemplates ?? []).map((template) => [template.id, template])
  );
  const requestTaskSchemasById = objectFromEntries(
    (requestTaskSchemas ?? []).map((schema) => [schema.id, schema])
  );

  return (
    <RequestsSidepanel
      {...props}
      requestTemplatesById={requestTemplatesById}
      requestTaskSchemasById={requestTaskSchemasById}
    />
  );
}

export default withSearching(
  RequestsSidepanelConnector.connect(QueryingRequestsSidepanel),
  SearchType.REQUEST,
  NavConstants.Section.REQUESTS,
  {defaultSearchParams: defaultRequestsSearchParams(), getNavigationUpdate: getRequestsURL}
) as React.ComponentType<QueryingRequestsSidepanelProps>;
